// ST-32 -Claude- AI Agents view. GET /api/v2/agents/{status,health,usage-stats,conversations}
import { useQuery } from '@tanstack/react-query';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { getAgentStatus, getAgentHealth, getAgentUsageStats, getAgentConversations } from '../api';

export function AdminAgentsPage() {
  const status = useQuery({ queryKey: ['admin', 'agent-status'], queryFn: getAgentStatus });
  const health = useQuery({ queryKey: ['admin', 'agent-health'], queryFn: getAgentHealth });
  const usage = useQuery({ queryKey: ['admin', 'agent-usage'], queryFn: () => getAgentUsageStats() });
  const convos = useQuery({ queryKey: ['admin', 'agent-conversations'], queryFn: getAgentConversations });

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">AI Agents</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Read from <code className="text-xs px-1 bg-slate-100 dark:bg-slate-800 rounded">/api/v2/agents/*</code>
      </p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Provider status */}
        <Card padded>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Provider status
          </h2>
          {status.isLoading && <LoadingSkeleton className="h-20 w-full" />}
          {status.error && <div className="text-sm text-red-500">{(status.error as Error).message}</div>}
          {status.data && (
            <div className="space-y-2">
              {(status.data.providers ?? []).length === 0 ? (
                <EmptyState title="No providers" />
              ) : (
                (status.data.providers ?? []).map((p) => (
                  <div key={p.name} className="flex items-center justify-between text-sm border-b border-slate-100 dark:border-slate-800 pb-2 last:border-0">
                    <div>
                      <div className="font-medium text-slate-900 dark:text-slate-100">{p.name}</div>
                      {p.model && <div className="text-xs text-slate-500 dark:text-slate-400">{p.model}</div>}
                    </div>
                    {p.isAvailable ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">● Available</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">● Unavailable</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Health */}
        <Card padded>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Health checks
          </h2>
          {health.isLoading && <LoadingSkeleton className="h-20 w-full" />}
          {health.error && <div className="text-sm text-red-500">{(health.error as Error).message}</div>}
          {health.data && (
            <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-x-auto">
              {JSON.stringify(health.data, null, 2)}
            </pre>
          )}
        </Card>

        {/* Usage stats */}
        <Card padded className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Usage stats
          </h2>
          {usage.isLoading && <LoadingSkeleton className="h-32 w-full" />}
          {usage.error && <div className="text-sm text-red-500">{(usage.error as Error).message}</div>}
          {usage.data && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Requests</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{usage.data.totalRequests ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Input tokens</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{(usage.data.totalInputTokens ?? 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Output tokens</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{(usage.data.totalOutputTokens ?? 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Cost (USD)</div>
                <div className="text-2xl font-bold text-orange-500">${(usage.data.totalCostUsd ?? 0).toFixed(2)}</div>
              </div>
            </div>
          )}
        </Card>

        {/* Conversations */}
        <Card padded className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Recent conversations
          </h2>
          {convos.isLoading && <LoadingSkeleton className="h-20 w-full" />}
          {convos.error && (
            <div className="text-sm text-red-500">{(convos.error as Error).message}</div>
          )}
          {convos.data && (convos.data.length === 0 ? (
            <EmptyState
              title="No persisted conversations"
              description="Phase 1 of the agent system stores conversations in-memory only. Phase 2 will persist them to the database for admin review."
            />
          ) : (
            <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-x-auto max-h-96">
              {JSON.stringify(convos.data, null, 2)}
            </pre>
          ))}
        </Card>
      </div>
    </div>
  );
}
